/**
 * src/lib/useReveal.js
 *
 * "Everything fades up into view; nothing simply appears."
 *
 * One IntersectionObserver for the whole page, added once on mount, that adds
 * `is-in` to any `.rp-reveal` element as it enters the viewport. The CSS in
 * index.css does the rest.
 *
 * WHY AN OBSERVER AND NOT A CSS ANIMATION
 *
 * A pure `animation` fires on load whether or not the element is on screen, so
 * everything below the fold has already finished animating by the time you
 * scroll to it. The whole point of the effect is the arrival.
 *
 * WHY IT RE-SCANS
 *
 * Funnel pages render lists from data that loads after mount (testimonials,
 * form steps). A MutationObserver picks up `.rp-reveal` nodes added later; the
 * alternative was every consumer remembering to call a refresh function, and
 * the failure mode of forgetting is an element stuck invisible at opacity 0.
 *
 * Reduced motion is handled in CSS rather than here — `.rp-reveal` resolves to
 * fully visible with no transition under `prefers-reduced-motion`, so even if
 * the observer never fires nothing is hidden.
 */

import { useEffect } from "react";

const SELECTOR = ".rp-reveal:not(.is-in)";

export default function useReveal() {
  useEffect(() => {
    // No IntersectionObserver (old browser, some test environments): show
    // everything immediately rather than leaving the page blank.
    if (typeof IntersectionObserver === "undefined") {
      document.querySelectorAll(SELECTOR).forEach((el) => el.classList.add("is-in"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          // Stagger siblings so a list arrives as a sequence rather than a
          // block. The index is read off the element so a consumer can order
          // its own children without this hook knowing the markup.
          const delay = Number(entry.target.dataset.revealDelay || 0);
          if (delay > 0) {
            entry.target.style.transitionDelay = `${delay}ms`;
          }
          entry.target.classList.add("is-in");
          observer.unobserve(entry.target);
        }
      },
      // Fire slightly before the element reaches the bottom edge, so the
      // motion reads as the element arriving rather than catching up.
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 },
    );

    const scan = () => {
      document.querySelectorAll(SELECTOR).forEach((el) => observer.observe(el));
    };

    scan();

    const mutations = new MutationObserver(scan);
    mutations.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutations.disconnect();
      observer.disconnect();
    };
  }, []);
}
